/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// pages/dashboard/ApplicationList.tsx
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useApplication } from "../../context/ApplicationContext";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";

interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: "pending" | "reviewed" | "accepted" | "rejected" | "withdrawn";
  coverLetter?: string;
  resumeUrl?: string;
  createdAt: string;
  updatedAt: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    type: string;
    category: string;
    deadline?: string;
    createdAt: string;
    postedBy: {
      id: string;
      name: string;
      email: string;
    };
  };
}

export default function ApplicationList() {
  const {
    applications,
    loading,
    error,
    refreshApplications,
    withdrawApplication,
    updateApplication,
  } = useApplication();

  const [filter, setFilter] = useState<
    "all" | "pending" | "reviewed" | "accepted" | "rejected" | "withdrawn"
  >("all");
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const navigate = useNavigate();
  const { isDark } = useDarkMode();

  // Function to handle viewing similar jobs
  const handleViewSimilarJobs = (category: string) => {
    navigate(`/dashboard/find-job?category=${encodeURIComponent(category)}`);
  };

  // Function to toggle card expansion
  const toggleCardExpansion = (applicationId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  useAuth();

  // Stop local loading after 3 seconds or when applications are loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      setLocalLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (applications.length > 0 || !loading) {
      setLocalLoading(false);
    }
  }, [applications, loading]);

  // Check for success message in URL (from ApplyJobForm redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    if (success === "true") {
      setSuccessMessage("Application submitted successfully!");
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage("");
        // Clean up URL
        window.history.replaceState({}, "", window.location.pathname);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Clear error messages after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // ✅ ENHANCED: Withdraw application with better error handling and optimistic updates
  const handleWithdrawApplication = async (applicationId: string) => {
    if (
      !confirm(
        "Are you sure you want to withdraw this application? This action cannot be undone.",
      )
    ) {
      return;
    }

    setWithdrawing(applicationId);
    setErrorMessage("");

    try {
      // Optimistic update - immediately show withdrawn status
      const applicationToUpdate = applications.find(
        (app) => app.id === applicationId,
      );
      if (applicationToUpdate) {
        updateApplication(applicationId, {
          status: "withdrawn",
          updatedAt: new Date().toISOString(),
        });
      }

      const result = await withdrawApplication(applicationId);

      if (!result.success) {
        // Revert optimistic update if failed
        if (applicationToUpdate) {
          updateApplication(applicationId, {
            status: applicationToUpdate.status,
            updatedAt: applicationToUpdate.updatedAt,
          });
        }
        throw new Error(result.error || "Failed to withdraw application");
      }

      // Show success message
      setSuccessMessage("Application withdrawn successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);

      // Refresh applications to ensure data consistency
      setTimeout(() => {
        refreshApplications();
      }, 1000);
    } catch (err: any) {
      console.error("Error withdrawing application:", err);
      const errorMsg = err.message || "Failed to withdraw application";
      setErrorMessage(errorMsg);

      // Show error alert only for critical errors
      if (
        !err.message?.includes("already") &&
        !err.message?.includes("cannot")
      ) {
        alert(errorMsg);
      }
    } finally {
      setWithdrawing(null);
    }
  };

  const handleRefreshApplications = async () => {
    try {
      setErrorMessage("");
      await refreshApplications();
      setSuccessMessage("Applications refreshed successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      console.error("Error refreshing applications:", err);
      setErrorMessage("Failed to refresh applications");
    }
  };

  // ✅ FIXED: Include withdrawn applications in filter
  const filteredApplications = applications.filter(
    (app) => filter === "all" || app.status === filter,
  );

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        light: "bg-yellow-100 text-yellow-800 border-yellow-200",
        dark: "bg-yellow-900/30 text-yellow-300 border-yellow-700/50",
        text: "Pending Review",
        icon: "⏳",
      },
      reviewed: {
        light: "bg-blue-100 text-blue-800 border-blue-200",
        dark: "bg-blue-900/30 text-blue-300 border-blue-700/50",
        text: "Under Review",
        icon: "👀",
      },
      accepted: {
        light: "bg-green-100 text-green-800 border-green-200",
        dark: "bg-green-900/30 text-green-300 border-green-700/50",
        text: "Accepted",
        icon: "✅",
      },
      rejected: {
        light: "bg-red-100 text-red-800 border-red-200",
        dark: "bg-red-900/30 text-red-300 border-red-700/50",
        text: "Not Selected",
        icon: "❌",
      },
      withdrawn: {
        light: "bg-gray-100 text-gray-800 border-gray-200",
        dark: "bg-gray-800 text-gray-300 border-gray-600",
        text: "Withdrawn",
        icon: "↩️",
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${
          isDark ? config.dark : config.light
        }`}
      >
        <span>{config.icon}</span>
        {config.text}
      </span>
    );
  };

  const getStatusCounts = () => {
    const counts = {
      all: applications.length,
      pending: applications.filter((app) => app.status === "pending").length,
      reviewed: applications.filter((app) => app.status === "reviewed").length,
      accepted: applications.filter((app) => app.status === "accepted").length,
      rejected: applications.filter((app) => app.status === "rejected").length,
      withdrawn: applications.filter((app) => app.status === "withdrawn")
        .length,
    };
    return counts;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getJobTypeDisplay = (type: string) => {
    const typeMap: { [key: string]: string } = {
      full_time: "Full-time",
      part_time: "Part-time",
      contract: "Contract",
      freelance: "Freelance",
      internship: "Internship",
      remote: "Remote",
      temporary: "Temporary",
    };
    return typeMap[type] || type;
  };

  // ✅ ENHANCED: Check if application can be withdrawn
  const canWithdrawApplication = (application: Application): boolean => {
    return (
      application.status === "pending" || application.status === "reviewed"
    );
  };

  const statusCounts = getStatusCounts();

  // Show loading only for the first few seconds
  const showLoading = localLoading || (loading && applications.length === 0);

  if (showLoading) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} py-8`}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className={isDark ? "text-gray-300" : "text-gray-600"}>
              Loading your applications...
            </p>
            <p
              className={`text-sm mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              This may take a moment
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-50"} py-8`}
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"} mb-2`}
          >
            My Applications
          </h1>
          <p className={isDark ? "text-gray-300" : "text-gray-600"}>
            Track the status of all your job applications in one place.
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div
            className={`${isDark ? "bg-green-900/30 border-green-700 text-green-300" : "bg-green-100 border-green-400 text-green-700"} border px-4 py-3 rounded mb-6`}
          >
            <div className="flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage("")}
                className={
                  isDark
                    ? "text-green-400 hover:text-green-200"
                    : "text-green-500 hover:text-green-700"
                }
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            className={`${isDark ? "bg-red-900/30 border-red-700 text-red-300" : "bg-red-100 border-red-400 text-red-700"} border px-4 py-3 rounded mb-6`}
          >
            <div className="flex justify-between items-center">
              <span>{errorMessage}</span>
              <button
                onClick={() => setErrorMessage("")}
                className={
                  isDark
                    ? "text-red-400 hover:text-red-200"
                    : "text-red-500 hover:text-red-700"
                }
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {[
            { key: "all" as const, label: "Total", color: "gray" },
            { key: "pending" as const, label: "Pending", color: "yellow" },
            { key: "reviewed" as const, label: "Under Review", color: "blue" },
            { key: "accepted" as const, label: "Accepted", color: "green" },
            { key: "rejected" as const, label: "Not Selected", color: "red" },
            { key: "withdrawn" as const, label: "Withdrawn", color: "gray" },
          ].map(({ key, label, color }) => {
            const isActive = filter === key;
            const activeClasses = isDark
              ? `border-${color}-500 shadow-lg shadow-${color}-500/20`
              : `border-${color}-500 shadow-md`;
            const inactiveClasses = isDark
              ? "border-gray-700 hover:border-gray-500 bg-gray-800"
              : "border-gray-200 hover:border-gray-300 bg-white";

            return (
              <div
                key={key}
                className={`rounded-lg p-4 border-2 cursor-pointer transition-all ${
                  isActive ? activeClasses : inactiveClasses
                }`}
                onClick={() => setFilter(key)}
              >
                <div
                  className={`text-2xl font-bold ${
                    isDark ? `text-${color}-400` : `text-${color}-600`
                  }`}
                >
                  {statusCounts[key]}
                </div>
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  {label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Global Error Message */}
        {error && (
          <div
            className={`${isDark ? "bg-red-900/30 border-red-700 text-red-300" : "bg-red-100 border-red-400 text-red-700"} border px-4 py-3 rounded mb-6`}
          >
            <div className="flex justify-between items-center">
              <span>Error loading applications: {error}</span>
              <button
                onClick={handleRefreshApplications}
                className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Applications List */}
        {filteredApplications.length > 0 ? (
          <div className="space-y-6">
            {filteredApplications.map((application) => {
              const isExpanded = expandedCards.has(application.id);
              const canWithdraw = canWithdrawApplication(application);

              return (
                <div
                  key={application.id}
                  className={`rounded-lg shadow-md border overflow-hidden hover:shadow-lg transition-all duration-300 ${
                    isDark
                      ? "bg-gray-800 border-gray-700 hover:shadow-gray-900/50"
                      : "bg-white border-gray-200"
                  } ${
                    isExpanded
                      ? isDark
                        ? "ring-2 ring-blue-400"
                        : "ring-2 ring-blue-500"
                      : ""
                  }`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Job Info */}
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 gap-3">
                          <div className="flex-1">
                            <h3
                              className={`text-xl font-semibold mb-1 ${
                                isDark ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {application.job.title}
                            </h3>
                            <p
                              className={`text-lg font-medium mb-2 ${
                                isDark ? "text-blue-400" : "text-blue-600"
                              }`}
                            >
                              {application.job.company} •{" "}
                              {application.job.location}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            {getStatusBadge(application.status)}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <p
                              className={
                                isDark ? "text-gray-300" : "text-gray-600"
                              }
                            >
                              <span className="font-medium">Job Type:</span>{" "}
                              {getJobTypeDisplay(application.job.type)}
                            </p>
                            <p
                              className={
                                isDark ? "text-gray-300" : "text-gray-600"
                              }
                            >
                              <span className="font-medium">Category:</span>{" "}
                              {application.job.category}
                            </p>
                            {application.job.salary && (
                              <p
                                className={
                                  isDark ? "text-gray-300" : "text-gray-600"
                                }
                              >
                                <span className="font-medium">Salary:</span>{" "}
                                {application.job.salary}
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <p
                              className={
                                isDark ? "text-gray-300" : "text-gray-600"
                              }
                            >
                              <span className="font-medium">Applied:</span>{" "}
                              {formatDate(application.createdAt)}
                            </p>
                            <p
                              className={
                                isDark ? "text-gray-300" : "text-gray-600"
                              }
                            >
                              <span className="font-medium">Last Updated:</span>{" "}
                              {formatDate(application.updatedAt)}
                            </p>
                            {application.job.deadline && (
                              <p
                                className={
                                  isDark ? "text-gray-300" : "text-gray-600"
                                }
                              >
                                <span className="font-medium">Deadline:</span>{" "}
                                {formatDate(application.job.deadline)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div
                            className={`mt-6 p-4 rounded-lg border ${
                              isDark
                                ? "bg-gray-700/50 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <h4
                              className={`font-semibold mb-3 text-lg ${
                                isDark ? "text-white" : "text-gray-900"
                              }`}
                            >
                              📋 Full Job Details
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-3">
                                <div>
                                  <h5
                                    className={`font-medium mb-2 ${
                                      isDark ? "text-gray-300" : "text-gray-700"
                                    }`}
                                  >
                                    Job Information
                                  </h5>
                                  <div
                                    className={`space-y-2 text-sm ${
                                      isDark ? "text-gray-400" : "text-gray-600"
                                    }`}
                                  >
                                    <p>
                                      <span className="font-medium">
                                        Company:
                                      </span>{" "}
                                      {application.job.company}
                                    </p>
                                    <p>
                                      <span className="font-medium">
                                        Location:
                                      </span>{" "}
                                      {application.job.location}
                                    </p>
                                    <p>
                                      <span className="font-medium">Type:</span>{" "}
                                      {getJobTypeDisplay(application.job.type)}
                                    </p>
                                    <p>
                                      <span className="font-medium">
                                        Category:
                                      </span>{" "}
                                      {application.job.category}
                                    </p>
                                    {application.job.salary && (
                                      <p>
                                        <span className="font-medium">
                                          Salary:
                                        </span>{" "}
                                        {application.job.salary}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-3">
                                {application.coverLetter && (
                                  <div>
                                    <h5
                                      className={`font-medium mb-2 ${
                                        isDark
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      Your Cover Letter
                                    </h5>
                                    <div
                                      className={`p-3 rounded border max-h-40 overflow-y-auto ${
                                        isDark
                                          ? "bg-gray-800 border-gray-600"
                                          : "bg-white border-gray-200"
                                      }`}
                                    >
                                      <p
                                        className={`text-sm whitespace-pre-wrap ${
                                          isDark
                                            ? "text-gray-300"
                                            : "text-gray-600"
                                        }`}
                                      >
                                        {application.coverLetter}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cover Letter Preview */}
                        {!isExpanded && application.coverLetter && (
                          <div className="mb-4">
                            <h4
                              className={`font-medium mb-2 ${
                                isDark ? "text-white" : "text-gray-900"
                              }`}
                            >
                              Your Cover Letter:
                            </h4>
                            <p
                              className={`text-sm line-clamp-3 ${
                                isDark ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {application.coverLetter}
                            </p>
                            {application.coverLetter.length > 200 && (
                              <button
                                onClick={() =>
                                  toggleCardExpansion(application.id)
                                }
                                className={`text-sm mt-1 ${
                                  isDark
                                    ? "text-blue-400 hover:text-blue-300"
                                    : "text-blue-500 hover:text-blue-700"
                                }`}
                              >
                                Read full cover letter
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() =>
                          handleViewSimilarJobs(application.job.category)
                        }
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                      >
                        View Similar Jobs
                      </button>

                      <button
                        onClick={() => toggleCardExpansion(application.id)}
                        className={`px-4 py-2 border rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${
                          isDark
                            ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {isExpanded ? (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 15l7-7 7 7"
                              />
                            </svg>
                            Show Less
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            View Job Details
                          </>
                        )}
                      </button>

                      {/* ✅ ENHANCED: Withdraw button with better state management */}
                      {canWithdraw && (
                        <button
                          onClick={() =>
                            handleWithdrawApplication(application.id)
                          }
                          disabled={withdrawing === application.id}
                          className={`px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium ${
                            isDark
                              ? "border-red-700 text-red-400 hover:bg-red-900/30"
                              : "border-red-300 text-red-700 hover:bg-red-50"
                          }`}
                        >
                          {withdrawing === application.id ? (
                            <span className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-700"></div>
                              Withdrawing...
                            </span>
                          ) : (
                            "Withdraw Application"
                          )}
                        </button>
                      )}

                      {/* Show withdrawn status if already withdrawn */}
                      {application.status === "withdrawn" && (
                        <span
                          className={`px-4 py-2 border rounded-lg text-sm font-medium ${
                            isDark
                              ? "border-gray-600 text-gray-400"
                              : "border-gray-300 text-gray-500"
                          }`}
                        >
                          Application Withdrawn
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* No Applications Message */
          <div className="text-center py-12">
            <div
              className={`rounded-lg shadow-sm border p-8 max-w-md mx-auto ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="text-6xl mb-4">📝</div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {filter === "all"
                  ? "No Applications Yet"
                  : `No ${filter} Applications`}
              </h3>
              <p
                className={`mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                {filter === "all"
                  ? error
                    ? "There was an error loading your applications. Please try refreshing."
                    : "You haven't applied to any jobs yet. Start browsing available positions to apply."
                  : `You don't have any ${filter} applications at the moment.`}
              </p>
              <div className="flex gap-3 justify-center">
                {error && (
                  <button
                    onClick={handleRefreshApplications}
                    className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                  >
                    Retry Loading
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard/find-job")}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  Browse Jobs
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Application Tips */}
        {applications.length > 0 && (
          <div
            className={`mt-8 rounded-lg p-6 ${
              isDark ? "bg-blue-900/20 border border-blue-800/50" : "bg-blue-50"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-3 ${
                isDark ? "text-blue-300" : "text-blue-900"
              }`}
            >
              Application Tips
            </h3>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm ${
                isDark ? "text-blue-200" : "text-blue-800"
              }`}
            >
              <div>
                <p className="font-medium mb-2">📊 Status Guide:</p>
                <ul className="space-y-1">
                  <li>
                    • <strong>Pending:</strong> Waiting for employer review
                  </li>
                  <li>
                    • <strong>Under Review:</strong> Employer is reviewing your
                    application
                  </li>
                  <li>
                    • <strong>Accepted:</strong> You've been selected!
                  </li>
                  <li>
                    • <strong>Not Selected:</strong> Position filled or not the
                    right fit
                  </li>
                  <li>
                    • <strong>Withdrawn:</strong> You've withdrawn this
                    application
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-medium mb-2">💡 Pro Tips:</p>
                <ul className="space-y-1">
                  <li>
                    • You can withdraw applications that are Pending or Under
                    Review
                  </li>
                  <li>• Withdrawn applications cannot be reactivated</li>
                  <li>• Keep your profile updated for better chances</li>
                  <li>• Check back regularly for status updates</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
